import * as THREE from 'three'

export class ScrollController {
  constructor(scene /*, fog */) {
    this.scene = scene
    // this.fog = fog
    
    // Camera tracking
    this.cameraZ = 5 // Start at Hero viewing position
    this.targetZ = 5
    
    // Section targets (camera positions for each section)
    this.cameraTargets = [5, -20, -45, -70]
    
    // Magnet scroll
    this.lastScrollTime = 0
    this.magnetDelay = 200 // ms before magnet activates
    this.magnetLerpFactor = 0.08
    
    // Mobile swipe
    this.touchStartY = 0
    this.touchStartTime = 0
    this.isSwiping = false
    this.swipeThreshold = 50 // px
    this.swipeMaxDuration = 600 // ms
    
    // Section configuration (fixed positions)
    this.sectionSpacing = 25
    this.sectionCount = 4
    
    // Current section tracking
    this.currentSectionIndex = 0
    this.onSectionChange = null
    this.onScroll = null
    
    // Warp transition state
    this.isWarping = false
    this.warpProgress = 0
    this.hasReset = false
    this.warpSpeedLines = null
    this.originalFogDensity = 0 // fog ? fog.density : 0
    
    this.setupWheelHandler()
    this.createWarpSpeedLines()
  }
  
  createWarpSpeedLines() {
    // Create particle system for warp speed lines
    const geometry = new THREE.BufferGeometry()
    const count = 200
    const positions = new Float32Array(count * 3)
    
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20
      positions[i * 3 + 2] = Math.random() * 50 - 25
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    
    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.1,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending
    })
    
    this.warpSpeedLines = new THREE.Points(geometry, material)
    this.warpSpeedLines.visible = false
    this.scene.add(this.warpSpeedLines)
  }
  
  setupWheelHandler() {
    window.addEventListener('wheel', (e) => {
      if (this.isWarping) return // Block input during warp
      
      const delta = e.deltaY * 0.01
      this.targetZ -= delta * 2
      this.lastScrollTime = performance.now()
      
      // Clamp to section bounds — no looping
      this.targetZ = Math.max(this.cameraTargets[this.cameraTargets.length - 1], Math.min(this.cameraTargets[0], this.targetZ))
      
      e.preventDefault()
    }, { passive: false })
    
    let lastTouchY = 0
    window.addEventListener('touchstart', (e) => {
      this.touchStartY = e.touches[0].clientY
      lastTouchY = this.touchStartY
      this.touchStartTime = performance.now()
      this.isSwiping = true
    }, { passive: true })
    
    window.addEventListener('touchmove', (e) => {
      if (this.isWarping) return
      
      const touchY = e.touches[0].clientY
      const delta = (lastTouchY - touchY) * 0.06
      this.targetZ -= delta * 2
      this.lastScrollTime = performance.now()
      
      // Clamp to section bounds — no looping
      this.targetZ = Math.max(this.cameraTargets[this.cameraTargets.length - 1], Math.min(this.cameraTargets[0], this.targetZ))
      
      lastTouchY = touchY
      e.preventDefault()
    }, { passive: false })
    
    window.addEventListener('touchend', (e) => {
      if (!this.isSwiping) return
      
      const touch = e.changedTouches[0]
      const swipeDistance = this.touchStartY - touch.clientY
      const swipeDuration = performance.now() - this.touchStartTime
      
      if (Math.abs(swipeDistance) > this.swipeThreshold && swipeDuration < this.swipeMaxDuration) {
        const direction = swipeDistance > 0 ? 1 : -1
        const newIndex = Math.max(0, Math.min(this.cameraTargets.length - 1, this.currentSectionIndex + direction))
        this.targetZ = this.cameraTargets[newIndex]
      }
      
      this.isSwiping = false
    }, { passive: true })
  }
  
  update(delta = 0.016) {
    // Normal camera movement
    this.cameraZ += (this.targetZ - this.cameraZ) * 0.08
    
    // Magnet scroll: ease targetZ toward nearest section target after scroll stops
    const now = performance.now()
    if (now - this.lastScrollTime > this.magnetDelay) {
      let nearestTarget = this.cameraTargets[0]
      let minTargetDistance = Math.abs(this.targetZ - nearestTarget)
      
      for (let i = 1; i < this.cameraTargets.length; i++) {
        const distance = Math.abs(this.targetZ - this.cameraTargets[i])
        if (distance < minTargetDistance) {
          minTargetDistance = distance
          nearestTarget = this.cameraTargets[i]
        }
      }
      
      if (minTargetDistance > 0.05) {
        this.targetZ += (nearestTarget - this.targetZ) * this.magnetLerpFactor
      } else {
        this.targetZ = nearestTarget
      }
    }
    
    // Section detection
    let closestIndex = 0
    let minDistance = Math.abs(this.cameraZ - this.cameraTargets[0])
    
    for (let i = 1; i < this.cameraTargets.length; i++) {
      const distance = Math.abs(this.cameraZ - this.cameraTargets[i])
      if (distance < minDistance) {
        minDistance = distance
        closestIndex = i
      }
    }
    
    if (closestIndex !== this.currentSectionIndex) {
      this.currentSectionIndex = closestIndex
      if (this.onSectionChange) {
        this.onSectionChange(this.currentSectionIndex, this.getCurrentSection())
      }
    }
    
    if (this.onScroll) {
      this.onScroll(this.cameraZ)
    }
    
    return this.cameraZ
  }
  
  startWarp(direction = 'forward') {
    this.isWarping = true
    this.warpProgress = 0
    this.hasReset = false
    this.warpDirection = direction
    
    // Show speed lines
    this.warpSpeedLines.visible = true
    
    console.log(`WARP START: ${direction}`)
  }
  
  updateWarp(delta) {
    this.warpProgress += delta * 2 // Warp lasts ~0.5 seconds
    
    // Update warp visuals
    const t = this.warpProgress
    
    // Fog density increases during warp (disabled)
    // if (this.fog) {
    //   if (t < 0.5) {
    //     // Ramping up
    //     this.fog.density = this.originalFogDensity + (0.15 * (t * 2))
    //   } else {
    //     // Ramping down
    //     this.fog.density = this.originalFogDensity + (0.15 * ((1 - t) * 2))
    //   }
    // }
    
    // Speed lines opacity
    if (t < 0.2) {
      this.warpSpeedLines.material.opacity = t * 5
    } else if (t > 0.8) {
      this.warpSpeedLines.material.opacity = (1 - t) * 5
    } else {
      this.warpSpeedLines.material.opacity = 1
    }
    
    // Animate speed lines rushing toward camera
    const positions = this.warpSpeedLines.geometry.attributes.position.array
    for (let i = 0; i < 200; i++) {
      positions[i * 3 + 2] += 2 // Move toward camera
      if (positions[i * 3 + 2] > 10) {
        positions[i * 3 + 2] = -40 // Reset to back
      }
    }
    this.warpSpeedLines.geometry.attributes.position.needsUpdate = true
    
    // Reset camera at peak warp (when fog is thickest)
    if (t > 0.5 && !this.hasReset) {
      if (this.warpDirection === 'forward') {
        // Forward: CTA → Hero
        this.cameraZ = 5
        this.targetZ = 5
        this.currentSectionIndex = 0
        console.log('WARP RESET - back to Hero')
      } else {
        // Backward: Hero → CTA
        this.cameraZ = -70
        this.targetZ = -70
        this.currentSectionIndex = 3
        console.log('WARP RESET - to CTA')
      }
      this.hasReset = true
      
      if (this.onSectionChange) {
        this.onSectionChange(this.currentSectionIndex, this.getCurrentSection())
      }
    }
    
    // End warp
    if (this.warpProgress >= 1) {
      this.isWarping = false
      this.warpProgress = 0
      this.warpSpeedLines.visible = false
      // if (this.fog) this.fog.density = this.originalFogDensity
      console.log('WARP END')
    }
    
    return this.cameraZ
  }
  
  getCurrentSection() {
    const names = ['hero', 'projects', 'experience', 'cta']
    return { 
      name: names[this.currentSectionIndex], 
      index: this.currentSectionIndex 
    }
  }
  
  destroy() {
    if (this.warpSpeedLines) {
      this.scene.remove(this.warpSpeedLines)
    }
  }
}
